/**
 * boundsHelper.ts
 * Shared geometric clipping and bounding box validation for 3D elements,
 * axes, price ticks, and period separators.
 */

export interface BoundingBox2D {
  minX?: number;
  maxX?: number;
  minY?: number;
  maxY?: number;
}

export interface Margin2D {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

/**
 * Checks if a 1D value falls strictly within [min, max] with optional margins.
 */
export function isWithin1DBounds(
  value: number,
  min?: number,
  max?: number,
  marginMin: number = 0,
  marginMax: number = 0
): boolean {
  if (min !== undefined && value < min + marginMin) return false;
  if (max !== undefined && value > max - marginMax) return false;
  return true;
}

/**
 * Checks if a 2D coordinate (x, y) falls strictly within a bounding box with optional margins.
 */
export function isWithin2DBounds(
  x: number,
  y: number,
  bounds: BoundingBox2D,
  margin: Margin2D = {}
): boolean {
  const { minX, maxX, minY, maxY } = bounds;
  const { top = 0, bottom = 0, left = 0, right = 0 } = margin;

  if (minX !== undefined && x < minX + left) return false;
  if (maxX !== undefined && x > maxX - right) return false;
  if (minY !== undefined && y < minY + bottom) return false;
  if (maxY !== undefined && y > maxY - top) return false;

  return true;
}

/**
 * Filters an array of ticks to ensure mapped positions strictly respect physical mesh bounds.
 */
export function filterTicksWithinBounds<T extends { value: number }>(
  ticks: T[],
  mapValue?: (val: number) => number,
  bounds?: [number, number],
  padding: number = 0
): T[] {
  if (!bounds || bounds.length < 2) return ticks;
  const [minBound, maxBound] = bounds;
  
  return ticks.filter((tick) => {
    const pos = mapValue ? mapValue(tick.value) : tick.value;
    return isWithin1DBounds(pos, minBound, maxBound, padding, padding);
  });
}
